import { useState, useEffect, useContext } from "react";
import { CartContext } from "../../store/context/CartContext";
import ApiService from "../../modules/APIService.interceptors";
import { useAuthStore } from "../../store/zustand/authStore";
import { useLive } from "../../hooks/useLives";
import { useLiveCategories } from "../../hooks/useLiveCategories";
import {
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

export const useCategories = () => {
  const user = useAuthStore((state) => state.user);
  const { setTitleSeccion } = useContext(CartContext);

  const [newLiveModal, setNewLiveModal] = useState(false);
  const [loader, setLoader] = useState(false);
  const [lives, setLives] = useState();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [liveToDelete, setLiveToDelete] = useState(null);
  const [showArchiveConfirmationModal, setShowArchiveConfirmationModal] =
    useState(false);
  const [liveToArchive, setLiveToArchive] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [liveToEdit, setLiveToEdit] = useState(null);

  const { uploadInfo, checkUploadStatusLocal, loading: uploadLoading } = useLive();
  const liveCategories = useLiveCategories();

  const canWrite = !!user?.admin_users?.LIVES?.WRITE?.[0];

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  // ── Data fetching ──
  const getAllLives = async () => {
    setLoader(true);
    try {
      const response = await ApiService.getAllLives();
      if (!response.error) setLives(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    checkUploadStatusLocal();
    const interval = setInterval(checkUploadStatusLocal, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setTitleSeccion("Categorias");
    getAllLives();
    liveCategories.getAll();
  }, []);

  // ── Lives actions ──
  const deleteLive = async () => {
    setLoader(true);
    try {
      const response = await ApiService.deleteLive(liveToDelete.id);
      if (!response.error) {
        setShowConfirmationModal(false);
        setLiveToDelete(null);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  const archiveLive = async () => {
    setLoader(true);
    try {
      const response = await ApiService.archiveLive(liveToArchive.id);
      if (!response.error) {
        setShowArchiveConfirmationModal(false);
        setLiveToArchive(null);
        setLives((prev) => prev.filter((live) => live.id !== liveToArchive.id));
        if (selectedCategory) {
          setSelectedCategory((prev) => ({
            ...prev,
            lives: prev.lives.filter((live) => live.id !== liveToArchive.id),
          }));
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  // ── Category selection ──
  const handleCategoryClick = (category) => setSelectedCategory(category);
  const handleCloseModalCategory = () => setSelectedCategory(null);

  // ── Edit live ──
  const startEditLive = (item) => {
    setLiveToEdit(item);
    setIsEditing(true);
  };

  const cancelEditLive = () => {
    setLiveToEdit(null);
    setIsEditing(false);
  };

  const onLiveSaved = (updated) => {
    setLives((prev) =>
      Array.isArray(prev)
        ? prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l))
        : prev
    );
    if (selectedCategory) {
      setSelectedCategory((prev) =>
        prev
          ? {
              ...prev,
              lives: prev.lives?.map((l) =>
                l.id === updated.id ? { ...l, ...updated } : l
              ),
            }
          : prev
      );
    }
  };

  // ── Archive confirmation ──
  const openArchiveConfirmation = (item) => {
    setShowArchiveConfirmationModal(true);
    setLiveToArchive(item);
  };

  return {
    // Auth
    user,
    canWrite,
    // Data
    lives,
    // Upload
    uploadLoading,
    uploadInfo,
    // Categories (from useLiveCategories)
    ...liveCategories,
    // DnD
    sensors,
    // Loading
    loader,
    // New live modal
    newLiveModal,
    setNewLiveModal,
    // Selected category
    selectedCategory,
    handleCategoryClick,
    handleCloseModalCategory,
    // Live editing
    isEditing,
    liveToEdit,
    startEditLive,
    cancelEditLive,
    onLiveSaved,
    // Delete live
    showConfirmationModal,
    setShowConfirmationModal,
    liveToDelete,
    setLiveToDelete,
    deleteLive,
    // Archive live
    showArchiveConfirmationModal,
    setShowArchiveConfirmationModal,
    liveToArchive,
    openArchiveConfirmation,
    archiveLive,
  };
};
